import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { FORM_ERROR } from 'final-form';
import { TextField } from 'mui-rff';
import { Form } from 'react-final-form';

import { breadcrumbsActions } from '@store/breadcrumbs';
import { usePermissions } from '@store/auth';
import { showSuccessMessage } from '@store/dashboard';
import ProdConfirmationDialog from '@components/ProdConfirmationDialog';

import ApplicationPageTemplate from '@dashboardComponents/ApplicationPageTemplate/ApplicationPageTemplate';
import {
  addNewGateway,
  getGatewayDetails,
  getGateways,
  paymentOptionsGatewaysActions,
  selectSelectedGateway,
  updateGateway,
} from '../../store/payment-options-gateways';
import {
  addGateway,
  editGateway,
  GATEWAY_LIST_ROUTE,
} from '../../payment-options.breadcrumbs';

import FormSaveAndSubmit from '../../../../components/FormSaveAndSubmit';
import { Typography } from '../../../../components/styledComponents';

const initForm = {
  code: '',
  name: '',
};

export default function GatewayForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const selectedGateway = useSelector(selectSelectedGateway);

  const { id } = useParams();

  const permissions = usePermissions();
  const canEdit = permissions?.canEditPaymentOptions;

  // A view-only user can still open an existing record, but must not reach the
  // create form directly via its URL.
  useEffect(() => {
    if (id === undefined && !permissions?.canEditPaymentOptions) {
      navigate(GATEWAY_LIST_ROUTE);
    }
  }, [id, navigate, permissions]);

  const [isFetchingData, setIsFetchingData] = useState(true);
  const [formValue, setFormValue] = useState(initForm);
  const [formSuccessMsg, setFormSuccessMsg] = useState('');
  const [formErrorMsg, setFormErrorMsg] = useState('');
  const [openDialog, setOpenDialog] = useState({ show: false });

  useEffect(() => {
    let isActive = true;
    setIsFetchingData(true);
    dispatch(breadcrumbsActions.showMenu(true));

    if (id !== undefined) {
      dispatch(breadcrumbsActions.setBreadcrumbs(editGateway(id)));
      Promise.all([
        dispatch(getGateways()),
        dispatch(getGatewayDetails(id)),
      ]).then(() => {
        if (isActive) setIsFetchingData(false);
      });
    } else {
      dispatch(breadcrumbsActions.setBreadcrumbs(addGateway));
      Promise.all([
        dispatch(getGateways()),
        dispatch(paymentOptionsGatewaysActions.resetSelectedGateway()),
      ]).then(() => {
        if (isActive) setIsFetchingData(false);
      });
    }

    return () => {
      isActive = false;
    };
  }, [id, dispatch]);

  useEffect(() => {
    if (id !== undefined) {
      setFormValue(selectedGateway);
    } else {
      setFormValue(initForm);
    }
  }, [selectedGateway, id]);

  const onSubmit = async (values) => {
    setFormSuccessMsg('');
    setFormErrorMsg('');

    const payload = {
      id,
      code: values.code,
      name: values.name,
    };

    try {
      const action =
        id !== undefined ? updateGateway(payload) : addNewGateway(payload);
      const result = await dispatch(action).unwrap();

      if (result.status === 'SUCCESS') {
        if (id === undefined) {
          dispatch(showSuccessMessage('Gateway added successfully'));
          navigate(GATEWAY_LIST_ROUTE);
        } else {
          setFormSuccessMsg('Gateway has been updated successfully');
          setFormValue(values); // needed to disable buttons after successful submit
        }
      } else {
        setFormErrorMsg(result.message || 'Operation failed');
      }
    } catch (error) {
      setFormErrorMsg(error.message || 'An error occurred');
    }
  };

  const validate = (values) => {
    if (!values.name) return { [FORM_ERROR]: 'Gateway Name is required' };
    if (!values.code) return { [FORM_ERROR]: 'Gateway Code is required' };
    return {};
  };

  const handlePreSubmit = (e) => {
    e.preventDefault();
    setOpenDialog({ show: true }); // open the confirmation dialog if prod
  };

  return (
    <ApplicationPageTemplate
      title={id !== undefined ? selectedGateway.name : 'New Gateway'}
      pageReady={!isFetchingData}>
      {!isFetchingData && (
        <Form
          onSubmit={onSubmit}
          initialValues={formValue}
          validate={validate}
          render={({ handleSubmit, pristine, invalid, form }) => (
            <form
              onSubmit={(e) =>
                process.env.REACT_APP_ENV !== 'production'
                  ? handleSubmit(e)
                  : handlePreSubmit(e)
              }
              noValidate>
              <ProdConfirmationDialog
                openDialog={openDialog}
                yesHandler={handleSubmit}
              />
              <Grid
                container
                spacing={1}
                style={{
                  alignContent: 'center',
                  justifyContent: 'space-between',
                }}>
                <Grid
                  item
                  xs={12}
                  md={8}
                  style={{ flex: 2 }}>
                  {formSuccessMsg && (
                    <Alert
                      onClose={() => {
                        setFormSuccessMsg('');
                      }}
                      severity="success">
                      {formSuccessMsg}
                    </Alert>
                  )}
                  {formErrorMsg && (
                    <Alert
                      onClose={() => {
                        setFormErrorMsg('');
                      }}
                      severity="error">
                      {formErrorMsg}
                    </Alert>
                  )}
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={4}
                  style={{ display: 'flex' }}>
                  <FormSaveAndSubmit
                    id={id}
                    resetDisabled={pristine}
                    submitDisabled={pristine || invalid || !canEdit}
                    handleReset={() => {
                      setFormSuccessMsg('');
                      setFormErrorMsg('');
                      form.reset();
                    }}
                  />
                </Grid>
              </Grid>
              <Box
                style={{
                  marginTop: '20px',
                  padding: '32px',
                  border: '1px solid #c4c4c4',
                  borderRadius: '4px',
                }}>
                <Typography
                  className="form-header"
                  variant="formHeader">
                  Details
                </Typography>
                <Grid
                  container
                  justifyContent="space-between"
                  alignItems="flex-start"
                  spacing={2}>
                  <Grid
                    item
                    xs={12}
                    md={6}>
                    <TextField
                      required
                      aria-label="gateway-name"
                      name="name"
                      variant="outlined"
                      label="Name"
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}>
                    <TextField
                      required
                      aria-label="gateway-code"
                      name="code"
                      variant="outlined"
                      label="Code"
                      helperText="Sample: RZR"
                      disabled={id !== undefined}
                    />
                  </Grid>
                </Grid>
              </Box>
            </form>
          )}
        />
      )}
    </ApplicationPageTemplate>
  );
}
